/**
 * shareVideoRecorder.js — renders a verse + ambience to a WebM video blob.
 *
 * Strategy:
 *   1. Animate a 1080×1920 canvas (vertical, TikTok/Reels-friendly) with a
 *      slowly drifting gradient, film-grain, and the verse text.
 *   2. Fetch OpenAI TTS narration as MP3 (optional, auth-gated) and play via
 *      an HTMLAudioElement routed through the shared AudioEffectsManager's
 *      AudioContext so we can pipe both narration and ambience into one
 *      MediaStreamDestination.
 *   3. Capture canvas.captureStream() + audio destination stream → one
 *      combined MediaStream → MediaRecorder (video/webm;codecs=vp9,opus).
 *   4. Stop after `durationMs` and resolve with the final Blob.
 *
 * Browser support: Chrome/Edge/Android Chrome — full. iOS Safari lacks
 * MediaRecorder (< iOS 17.4) → the caller is expected to check
 * `isVideoSupported()` first and show a friendly message otherwise.
 */

export const isVideoSupported = () => {
  try {
    return (
      typeof MediaRecorder !== 'undefined' &&
      typeof HTMLCanvasElement.prototype.captureStream === 'function' &&
      (MediaRecorder.isTypeSupported?.('video/webm;codecs=vp9,opus') ||
        MediaRecorder.isTypeSupported?.('video/webm;codecs=vp8,opus') ||
        MediaRecorder.isTypeSupported?.('video/webm'))
    );
  } catch (_) {
    return false;
  }
};

const pickMime = () => {
  const candidates = [
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
  ];
  for (const m of candidates) {
    if (MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(m)) return m;
  }
  return 'video/webm';
};

const wrapText = (ctx, text, maxWidth) => {
  const words = text.split(' ');
  const lines = [];
  let line = '';
  for (const w of words) {
    const candidate = line ? `${line} ${w}` : w;
    if (ctx.measureText(candidate).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else {
      line = candidate;
    }
  }
  if (line) lines.push(line);
  return lines;
};

/**
 * @param {Object} opts
 * @param {{reference:string, text:string, translation?:string}} opts.verse
 * @param {{grad:[string,string], accent:string, label:string}} opts.theme
 * @param {number} opts.durationMs
 * @param {AudioEffectsManager} opts.effectsManager
 * @param {string[]} opts.ambientLayers  names to start on the effects bus
 * @param {Blob|null} opts.narrationBlob  optional OpenAI-TTS MP3
 * @param {(pct:number)=>void} [opts.onProgress]
 * @returns {Promise<Blob>}
 */
export const recordShareVideo = async ({
  verse,
  theme,
  durationMs = 15000,
  effectsManager,
  ambientLayers = [],
  narrationBlob = null,
  onProgress,
}) => {
  if (!isVideoSupported()) throw new Error('MediaRecorder/captureStream unsupported');
  if (!verse || !theme) throw new Error('verse and theme are required');

  const W = 1080;
  const H = 1920;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');

  /* ------------------------- Build audio stream ------------------------- */
  // Reuse the effects manager's AudioContext so ambient layers are already
  // routed on the same bus we capture.
  const audioCtx = effectsManager?.audioContext;
  if (!audioCtx) throw new Error('AudioContext unavailable');
  if (audioCtx.state === 'suspended') {
    try { await audioCtx.resume(); } catch (_) { /* ignore */ }
  }
  const dest = audioCtx.createMediaStreamDestination();
  // Route master ambience bus into the destination (tap — don't disconnect from speakers).
  try { effectsManager.masterGain?.connect(dest); } catch (_) { /* already connected */ }

  // Optional narration via HTMLAudioElement → MediaElementAudioSourceNode → both speakers + destination
  let narrationEl = null;
  let narrationSrc = null;
  let narrationUrl = null;
  if (narrationBlob) {
    narrationEl = new Audio();
    narrationUrl = URL.createObjectURL(narrationBlob);
    narrationEl.src = narrationUrl;
    narrationEl.crossOrigin = 'anonymous';
    narrationSrc = audioCtx.createMediaElementSource(narrationEl);
    narrationSrc.connect(dest);
    narrationSrc.connect(audioCtx.destination); // so user can hear while recording
  }

  /* ------------------------- Start ambience ----------------------------- */
  effectsManager.stopAllEffects();
  effectsManager.syncLayers(ambientLayers.slice(0, 3), 3);

  /* ------------------------- Animation loop ----------------------------- */
  const startTs = performance.now();
  let rafId;

  const renderFrame = (t) => {
    const elapsed = t - startTs;
    const progress = Math.min(1, elapsed / durationMs);
    if (onProgress) onProgress(progress);

    // Animated vertical gradient (slow drift)
    const shift = Math.sin(elapsed / 3000) * 60;
    const grad = ctx.createLinearGradient(0, -shift, W, H + shift);
    grad.addColorStop(0, theme.grad[0]);
    grad.addColorStop(1, theme.grad[1]);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, H);

    // Breathing radial glow
    const glowR = 500 + Math.sin(elapsed / 2000) * 60;
    const glow = ctx.createRadialGradient(W * 0.6, H * 0.3, 40, W * 0.6, H * 0.3, glowR);
    glow.addColorStop(0, 'rgba(255,255,255,0.22)');
    glow.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, W, H);

    // Lightweight film grain (sparser than the PNG to save CPU)
    ctx.save();
    ctx.globalAlpha = 0.05;
    for (let i = 0; i < 800; i++) {
      ctx.fillStyle = Math.random() > 0.5 ? '#fff' : '#000';
      ctx.fillRect(Math.random() * W, Math.random() * H, 1, 1);
    }
    ctx.restore();

    // Top branding
    ctx.fillStyle = theme.accent;
    ctx.font = '600 34px -apple-system, "Segoe UI", Roboto, system-ui, sans-serif';
    ctx.textBaseline = 'top';
    ctx.textAlign = 'left';
    ctx.fillText('✦ LUMERA', 84, 110);

    // Scene pill
    const pillLabel = (theme.label || '').toUpperCase();
    ctx.font = '500 24px -apple-system, "Segoe UI", Roboto, system-ui, sans-serif';
    const pillW = ctx.measureText(pillLabel).width + 52;
    const pillH = 48;
    const pillX = W - 84 - pillW;
    const pillY = 110 - 10;
    ctx.fillStyle = 'rgba(255,255,255,0.14)';
    ctx.beginPath();
    ctx.roundRect(pillX, pillY, pillW, pillH, 24);
    ctx.fill();
    ctx.fillStyle = theme.accent;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(pillLabel, pillX + pillW / 2, pillY + pillH / 2 + 1);

    // Verse text (centered, auto-wrapped, auto-sized, subtle scale-in)
    const scale = 0.96 + Math.min(0.04, progress * 0.08);
    const maxWidth = W - 180;
    let fontSize = 72;
    let lines = [];
    while (fontSize > 32) {
      ctx.font = `600 ${fontSize}px Georgia, "Times New Roman", serif`;
      lines = wrapText(ctx, `"${verse.text}"`, maxWidth);
      const totalH = lines.length * fontSize * 1.3;
      if (totalH <= H * 0.5 && lines.length <= 14) break;
      fontSize -= 4;
    }
    ctx.save();
    ctx.translate(W / 2, H / 2);
    ctx.scale(scale, scale);
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const lineHeight = fontSize * 1.35;
    const totalH = lines.length * lineHeight;
    const startY = -totalH / 2 + lineHeight / 2;
    lines.forEach((line, i) => ctx.fillText(line, 0, startY + i * lineHeight));
    ctx.restore();

    // Reference
    ctx.font = '600 44px -apple-system, "Segoe UI", Roboto, system-ui, sans-serif';
    ctx.fillStyle = theme.accent;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(`— ${verse.reference}`, W / 2, H / 2 + (lines.length * lineHeight) / 2 + 100);

    // Footer
    ctx.font = '400 28px -apple-system, "Segoe UI", Roboto, system-ui, sans-serif';
    ctx.fillStyle = 'rgba(255,255,255,0.7)';
    ctx.fillText('Listen & meditate on Lumera', W / 2, H - 120);

    // Progress bar at very bottom (subtle)
    ctx.fillStyle = 'rgba(255,255,255,0.15)';
    ctx.fillRect(0, H - 6, W, 6);
    ctx.fillStyle = theme.accent;
    ctx.fillRect(0, H - 6, W * progress, 6);

    if (elapsed < durationMs) {
      rafId = requestAnimationFrame(renderFrame);
    }
  };

  /* ------------------------- Set up MediaRecorder ----------------------- */
  const videoStream = canvas.captureStream(30);
  // Combine video + audio tracks
  const combined = new MediaStream([
    ...videoStream.getVideoTracks(),
    ...dest.stream.getAudioTracks(),
  ]);

  const mimeType = pickMime();
  const chunks = [];
  const recorder = new MediaRecorder(combined, { mimeType, videoBitsPerSecond: 3_000_000 });
  recorder.ondataavailable = (e) => {
    if (e.data && e.data.size > 0) chunks.push(e.data);
  };

  const done = new Promise((resolve, reject) => {
    recorder.onerror = (e) => reject(e.error || new Error('Recorder error'));
    recorder.onstop = () => {
      try {
        const blob = new Blob(chunks, { type: mimeType });
        resolve(blob);
      } catch (e) {
        reject(e);
      }
    };
  });

  /* ------------------------- Go ---------------------------------------- */
  recorder.start(250);
  rafId = requestAnimationFrame(renderFrame);
  if (narrationEl) {
    try { await narrationEl.play(); } catch (_) { /* autoplay may block; capture still works */ }
  }

  // Stop at durationMs
  await new Promise((r) => setTimeout(r, durationMs));
  try { recorder.stop(); } catch (_) { /* ignore */ }
  cancelAnimationFrame(rafId);
  if (narrationEl) {
    try { narrationEl.pause(); } catch (_) { /* already paused */ }
    if (narrationUrl) URL.revokeObjectURL(narrationUrl);
  }
  // Keep the master bus connection — other UI may still be using it.
  // Just detach the destination tap.
  try { effectsManager.masterGain?.disconnect(dest); } catch (_) { /* ok */ }
  effectsManager.stopAllEffects();

  const blob = await done;
  if (onProgress) onProgress(1);
  return blob;
};
